/**
 * ThreiaSystem: manages the prayer mechanic for Threia's Blessing.
 *
 * The player can invoke Threia's aid by rapidly pressing/releasing the "H" key
 * while standing still. The system tracks press cadence, builds glow intensity
 * through 3 phases, and after a final sustained burst of presses determines
 * whether the blessing succeeds.
 *
 * With the Threia Totem: 100% blessing success and 100% life restore.
 * Without totem: 35% blessing success, 40% life restore on success.
 *
 * The blessing can only be attempted once per maze level.
 */

import Phaser from 'phaser';
import { Events, TEX_GLOW } from '../utils/constants.js';
import {
  THREIA_BLESSING_CHANCE_BASE,
  THREIA_BLESSING_CHANCE_TOTEM,
  THREIA_LIFE_RESTORE_CHANCE_BASE,
  THREIA_LIFE_RESTORE_CHANCE_TOTEM,
  THREIA_PRAYER_PRESS_INTERVAL,
  THREIA_PRAYER_GLOW_PHASES,
  THREIA_PRAYER_PHASE_PRESSES,
  THREIA_PRAYER_FINAL_PRESSES,
  THREIA_GLOW_BASE_ALPHA,
  THREIA_GLOW_MAX_ALPHA,
  THREIA_BLESSING_FLASH_DURATION,
  THREIA_BLESSING_TEXT_DURATION,
  AUDIO_THREIA_PRAYER_HUM,
  AUDIO_THREIA_BLESSING,
  AUDIO_THREIA_FAIL,
  AUDIO_RECOVER_LIFE,
  COLOR_THREIA_GLOW,
  COLOR_THREIA_GOLD,
  TILE_SIZE,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';

/**
 * Prayer states.
 * @enum {string}
 */
const PrayerState = Object.freeze({
  IDLE: 'idle',
  BUILDING: 'building',
  MAX_GLOW: 'max_glow',
  BLESSING: 'blessing',
  DONE: 'done',
});

export default class ThreiaSystem {
  /**
   * @param {Phaser.Scene} scene - The GameScene.
   * @param {Phaser.Events.EventEmitter} eventBus - Shared event bus.
   * @param {import('../systems/HealthSystem.js').default} healthSystem
   * @param {import('../entities/Player.js').default} player
   */
  constructor(scene, eventBus, healthSystem, player) {
    /** @type {Phaser.Scene} */
    this.scene = scene;

    /** @type {Phaser.Events.EventEmitter} */
    this.eventBus = eventBus;

    /** @type {import('../systems/HealthSystem.js').default} */
    this.healthSystem = healthSystem;

    /** @type {import('../entities/Player.js').default} */
    this.player = player;

    /** @type {PrayerState} */
    this._state = PrayerState.IDLE;

    /** @type {boolean} Whether the blessing has been used on this level. */
    this._blessingUsedThisLevel = false;

    /** @type {boolean} Whether the player holds the Threia Totem. */
    this._hasTotem = false;

    /** @type {number} Total H presses in the current prayer session. */
    this._pressCount = 0;

    /** @type {number} Current glow phase (0 = none, 1-3). */
    this._glowPhase = 0;

    /** @type {number} Presses counted in the current final phase. */
    this._finalPresses = 0;

    /** @type {number} Timestamp of the last H press. */
    this._lastPressTime = 0;

    /** @type {Phaser.Time.TimerEvent|null} Timeout for press cadence. */
    this._cadenceTimer = null;

    // --- Visual effect references ---
    /** @type {Phaser.GameObjects.Graphics|null} */
    this._glowGraphics = null;

    /** @type {Phaser.Tweens.Tween|null} */
    this._glowPulseTween = null;

    /** @type {number} Current glow alpha (tweened). */
    this._currentGlowAlpha = 0;

    /** @type {Phaser.GameObjects.Particles.ParticleEmitter|null} */
    this._prayerEmitter = null;

    /** @type {boolean} Whether the system has been destroyed. */
    this._destroyed = false;
  }

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  /**
   * Set whether the player has the Threia Totem.
   * @param {boolean} has
   */
  setHasTotem(has) {
    this._hasTotem = has;
  }

  /**
   * Set whether the blessing has been used this level.
   * @param {boolean} used
   */
  setBlessingUsed(used) {
    this._blessingUsedThisLevel = used;
  }

  /**
   * Whether the blessing has been used on this level.
   * @returns {boolean}
   */
  get blessingUsed() {
    return this._blessingUsedThisLevel;
  }

  /**
   * Whether a blessing sequence is currently in progress.
   * @returns {boolean}
   */
  get isActive() {
    return this._state !== PrayerState.IDLE && this._state !== PrayerState.DONE;
  }

  /**
   * Handle the player pressing "H".
   * Must only be called when the player is standing still and
   * input is not blocked by transitions.
   */
  onHPress() {
    if (this._destroyed) return;
    if (this._blessingUsedThisLevel) return;
    if (this._state === PrayerState.BLESSING || this._state === PrayerState.DONE) return;

    const now = Date.now();

    // Check cadence — if too much time has passed, reset and let this
    // keypress start a fresh prayer (fall through to the IDLE handler)
    if (this._state !== PrayerState.IDLE && now - this._lastPressTime > THREIA_PRAYER_PRESS_INTERVAL) {
      this._cancelPrayer();
      // Do NOT return — allow this press to begin a new prayer below
    }

    this._lastPressTime = now;
    this._resetCadenceTimer();

    if (this._state === PrayerState.IDLE) {
      // Begin prayer
      this._state = PrayerState.BUILDING;
      this._pressCount = 1;
      this._glowPhase = 0;
      this._finalPresses = 0;
      this._startGlowEffect();
      this._playHumSound();
      return;
    }

    if (this._state === PrayerState.BUILDING) {
      this._pressCount += 1;

      // Check if we've reached a new glow phase
      const targetPhase = Math.min(
        Math.floor(this._pressCount / THREIA_PRAYER_PHASE_PRESSES),
        THREIA_PRAYER_GLOW_PHASES,
      );

      if (targetPhase > this._glowPhase) {
        this._glowPhase = targetPhase;
        this._updateGlowIntensity();
        this.eventBus.emit(Events.THREIA_PRAYER_PHASE, this._glowPhase);

        if (this._glowPhase >= THREIA_PRAYER_GLOW_PHASES) {
          this._state = PrayerState.MAX_GLOW;
          this._finalPresses = 0;
        }
      } else {
        // Subtle visual feedback per press
        this._pulseGlow();
      }

      this._playHumSound();
      return;
    }

    if (this._state === PrayerState.MAX_GLOW) {
      this._finalPresses += 1;
      this._pulseGlow();
      this._playHumSound();

      if (this._finalPresses >= THREIA_PRAYER_FINAL_PRESSES) {
        // Prayer complete — determine blessing outcome
        this._state = PrayerState.BLESSING;
        this._performBlessing();
      }
    }
  }

  /**
   * Called when the player moves — cancels any active prayer.
   */
  onPlayerMoved() {
    if (this._state !== PrayerState.IDLE && this._state !== PrayerState.DONE) {
      this._cancelPrayer();
    }
  }

  /**
   * Reset for a new level.
   * @param {boolean} [preserveBlessingUsed=false] - If true, doesn't reset the used flag.
   */
  resetForNewLevel(preserveBlessingUsed = false) {
    this._cancelPrayer();
    if (!preserveBlessingUsed) {
      this._blessingUsedThisLevel = false;
    }
    this._state = PrayerState.IDLE;
  }

  /**
   * Update called each frame. Updates visual effects positions.
   */
  update() {
    if (this._destroyed) return;
    if (this._glowGraphics && this.player && this.player.active) {
      this._redrawGlow();
    }
    if (this._prayerEmitter && this.player && this.player.active) {
      this._prayerEmitter.setPosition(this.player.x, this.player.y);
    }
  }

  /**
   * Destroy all resources.
   */
  destroy() {
    this._destroyed = true;
    this._cleanupEffects();
    if (this._cadenceTimer) {
      this._cadenceTimer.remove(false);
      this._cadenceTimer = null;
    }
  }

  // ---------------------------------------------------------------
  // Private — Prayer flow
  // ---------------------------------------------------------------

  /**
   * Cancel the current prayer session and cleanup visuals.
   * @private
   */
  _cancelPrayer() {
    this._cleanupEffects();
    this._state = PrayerState.IDLE;
    this._pressCount = 0;
    this._glowPhase = 0;
    this._finalPresses = 0;

    if (this._cadenceTimer) {
      this._cadenceTimer.remove(false);
      this._cadenceTimer = null;
    }
  }

  /**
   * Reset the cadence timer. If H is not pressed again within the interval,
   * the prayer is cancelled.
   * @private
   */
  _resetCadenceTimer() {
    if (this._cadenceTimer) {
      this._cadenceTimer.remove(false);
      this._cadenceTimer = null;
    }
    if (!this.scene) return;

    this._cadenceTimer = this.scene.time.delayedCall(THREIA_PRAYER_PRESS_INTERVAL, () => {
      if (this._state === PrayerState.BUILDING || this._state === PrayerState.MAX_GLOW) {
        this._cancelPrayer();
      }
    });
  }

  /**
   * Execute the blessing — success or fail.
   * @private
   */
  _performBlessing() {
    this._blessingUsedThisLevel = true;

    // Remove the cadence timer
    if (this._cadenceTimer) {
      this._cadenceTimer.remove(false);
      this._cadenceTimer = null;
    }

    // Roll for success
    const successChance = this._hasTotem
      ? THREIA_BLESSING_CHANCE_TOTEM
      : THREIA_BLESSING_CHANCE_BASE;
    const success = Math.random() < successChance;

    this.eventBus.emit(Events.THREIA_BLESSING_START);

    if (success) {
      this._playBlessingSuccess();
    } else {
      this._playBlessingFail();
    }
  }

  // ---------------------------------------------------------------
  // Private — Blessing success sequence
  // ---------------------------------------------------------------

  /**
   * Play the full blessing success sequence:
   * 1. Screen fills with golden-white light
   * 2. Blinding white flash
   * 3. Light recedes from edges to center on player
   * 4. "Threia bless you!" text
   * 5. Full heal + possible life restore
   * 6. Totem consumed if held
   * @private
   */
  _playBlessingSuccess() {
    if (!this.scene) return;

    // Play celestial sound
    try {
      this.scene.sound.play(AUDIO_THREIA_BLESSING, { volume: getSfxVol(0.7) });
    } catch (_) { /* fallback */ }

    // Phase 1: Golden light fills screen from player outward
    const flash = this.scene.add.graphics();
    flash.setDepth(500);
    flash.setScrollFactor(0);
    flash.setAlpha(0);

    this.scene.tweens.add({
      targets: flash,
      alpha: 1,
      duration: THREIA_BLESSING_FLASH_DURATION * 0.4,
      ease: 'Quad.easeIn',
      onUpdate: () => {
        if (!flash || !this.scene) return;
        flash.clear();
        // Golden-white gradient expanding from center
        flash.fillStyle(0xfff8dc, flash.alpha * 0.6);
        flash.fillRect(0, 0, this.scene.scale.width, this.scene.scale.height);
        flash.fillStyle(0xffffff, flash.alpha * 0.3);
        flash.fillRect(0, 0, this.scene.scale.width, this.scene.scale.height);
      },
      onComplete: () => {
        if (!this.scene) return;

        // Phase 2: Blinding white flash
        flash.clear();
        flash.fillStyle(0xffffff, 0.9);
        flash.fillRect(0, 0, this.scene.scale.width, this.scene.scale.height);

        // Phase 3: Light recedes to center (fade out)
        this.scene.tweens.add({
          targets: flash,
          alpha: 0,
          duration: THREIA_BLESSING_FLASH_DURATION * 0.6,
          ease: 'Quad.easeOut',
          onUpdate: () => {
            if (!flash || !this.scene || !this.player) return;
            flash.clear();
            // Shrinking light circle centered on player
            const progress = 1 - flash.alpha;
            const maxR = Math.max(this.scene.scale.width, this.scene.scale.height);
            const r = maxR * (1 - progress);

            flash.fillStyle(0xfff8dc, flash.alpha * 0.5);
            flash.fillRect(0, 0, this.scene.scale.width, this.scene.scale.height);

            // Cut a dark circle that grows as light recedes
            if (r > 0) {
              flash.fillStyle(0xffffff, flash.alpha * 0.7);
              flash.fillCircle(this.player.x, this.player.y, r * 0.3);
            }
          },
          onComplete: () => {
            flash.destroy();
            this._applyBlessingEffects();
          },
        });
      },
    });
  }

  /**
   * Apply the actual blessing effects after the visual sequence.
   * @private
   */
  _applyBlessingEffects() {
    if (!this.scene || this._destroyed) return;

    // Cleanup prayer glow
    this._cleanupEffects();

    // Full health heal
    this.healthSystem.fullHeal();

    // Golden flash on player
    this._playPlayerBlessingGlow();

    // Play recovery sound
    try {
      this.scene.sound.play(AUDIO_RECOVER_LIFE, { volume: getSfxVol(0.5) });
    } catch (_) { /* fallback */ }

    // Roll for life restoration
    const lifeChance = this._hasTotem
      ? THREIA_LIFE_RESTORE_CHANCE_TOTEM
      : THREIA_LIFE_RESTORE_CHANCE_BASE;
    const restoreLife = Math.random() < lifeChance;

    if (restoreLife) {
      this.healthSystem.addLife();
    }

    // Show "Threia bless you!" text
    this._showBlessingText('Threia bless you!');

    // Emit success event
    this.eventBus.emit(Events.THREIA_BLESSING_SUCCESS, {
      lifeRestored: restoreLife,
      hadTotem: this._hasTotem,
    });

    // Consume totem if held
    if (this._hasTotem) {
      this._hasTotem = false;
      this.eventBus.emit(Events.THREIA_TOTEM_CONSUMED);
    }

    this._state = PrayerState.DONE;
  }

  // ---------------------------------------------------------------
  // Private — Blessing fail sequence
  // ---------------------------------------------------------------

  /**
   * Play the blessing fail sequence.
   * @private
   */
  _playBlessingFail() {
    if (!this.scene) return;

    // Play fail sound
    try {
      this.scene.sound.play(AUDIO_THREIA_FAIL, { volume: getSfxVol(0.5) });
    } catch (_) { /* fallback */ }

    // Brief dim flash
    const flash = this.scene.add.graphics();
    flash.setDepth(500);
    flash.setScrollFactor(0);
    flash.fillStyle(0xffffff, 0.15);
    flash.fillRect(0, 0, this.scene.scale.width, this.scene.scale.height);

    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 800,
      ease: 'Quad.easeOut',
      onComplete: () => {
        flash.destroy();
      },
    });

    // Cleanup prayer glow with a gentle fade
    this._cleanupEffects();

    // Show message
    this._showBlessingText('Threia is watching over you');

    this.eventBus.emit(Events.THREIA_BLESSING_FAIL);

    this._state = PrayerState.DONE;
  }

  // ---------------------------------------------------------------
  // Private — Visual effects
  // ---------------------------------------------------------------

  /**
   * Start the glow effect around the player.
   * @private
   */
  _startGlowEffect() {
    if (!this.scene || !this.player) return;

    // Create glow graphics
    this._glowGraphics = this.scene.add.graphics();
    this._glowGraphics.setDepth(this.player.depth - 1);
    this._currentGlowAlpha = THREIA_GLOW_BASE_ALPHA;
    this._redrawGlow();

    // Create subtle particle emitter
    try {
      this._prayerEmitter = this.scene.add.particles(
        this.player.x, this.player.y, TEX_GLOW, {
          lifespan: { min: 300, max: 600 },
          speed: { min: 5, max: 20 },
          angle: { min: 240, max: 300 },
          scale: { start: 0.2, end: 0 },
          alpha: { start: 0.5, end: 0 },
          frequency: 120,
          quantity: 1,
        },
      );
      this._prayerEmitter.setDepth(this.player.depth + 1);
    } catch (_) { /* fallback */ }
  }

  /**
   * Update the glow intensity for the current phase.
   * @private
   */
  _updateGlowIntensity() {
    if (!this.scene) return;

    const phaseRatio = this._glowPhase / THREIA_PRAYER_GLOW_PHASES;
    const targetAlpha = THREIA_GLOW_BASE_ALPHA +
      (THREIA_GLOW_MAX_ALPHA - THREIA_GLOW_BASE_ALPHA) * phaseRatio;

    // Tween to new intensity
    if (this._glowPulseTween) {
      this._glowPulseTween.stop();
      this._glowPulseTween = null;
    }

    this._glowPulseTween = this.scene.tweens.add({
      targets: this,
      _currentGlowAlpha: targetAlpha,
      duration: 300,
      ease: 'Sine.easeOut',
      onUpdate: () => {
        this._redrawGlow();
      },
    });

    // Increase particle frequency with phase
    if (this._prayerEmitter) {
      const freq = Math.max(30, 120 - this._glowPhase * 30);
      this._prayerEmitter.frequency = freq;
      this._prayerEmitter.quantity = Math.min(3, 1 + this._glowPhase);
    }
  }

  /**
   * Quick pulse effect on each H press.
   * @private
   */
  _pulseGlow() {
    if (!this.scene || !this._glowGraphics) return;

    const savedAlpha = this._currentGlowAlpha;

    this.scene.tweens.add({
      targets: this,
      _currentGlowAlpha: Math.min(1, savedAlpha + 0.15),
      duration: 80,
      yoyo: true,
      ease: 'Sine.easeOut',
      onUpdate: () => {
        this._redrawGlow();
      },
      onComplete: () => {
        this._currentGlowAlpha = savedAlpha;
        this._redrawGlow();
      },
    });
  }

  /**
   * Redraw the glow circle at the player's position.
   * @private
   */
  _redrawGlow() {
    if (!this._glowGraphics || !this.player) return;
    this._glowGraphics.clear();

    // Outer soft glow
    this._glowGraphics.fillStyle(COLOR_THREIA_GLOW, this._currentGlowAlpha * 0.3);
    this._glowGraphics.fillCircle(this.player.x, this.player.y, TILE_SIZE * 1.2);

    // Middle glow
    this._glowGraphics.fillStyle(COLOR_THREIA_GLOW, this._currentGlowAlpha * 0.5);
    this._glowGraphics.fillCircle(this.player.x, this.player.y, TILE_SIZE * 0.8);

    // Inner bright core
    this._glowGraphics.fillStyle(0xffffff, this._currentGlowAlpha * 0.7);
    this._glowGraphics.fillCircle(this.player.x, this.player.y, TILE_SIZE * 0.4);
  }

  /**
   * Play a golden glow burst on the player after blessing success.
   * @private
   */
  _playPlayerBlessingGlow() {
    if (!this.scene || !this.player) return;

    // Brief golden tint on the player
    this.player.setTint(COLOR_THREIA_GOLD);

    // Expanding glow ring
    const ring = this.scene.add.graphics();
    ring.setDepth(this.player.depth + 2);

    const ringObj = { radius: TILE_SIZE * 0.3, alpha: 0.8 };
    this.scene.tweens.add({
      targets: ringObj,
      radius: TILE_SIZE * 2.5,
      alpha: 0,
      duration: 800,
      ease: 'Quad.easeOut',
      onUpdate: () => {
        if (!ring || !this.player) return;
        ring.clear();
        ring.lineStyle(2, COLOR_THREIA_GOLD, ringObj.alpha);
        ring.strokeCircle(this.player.x, this.player.y, ringObj.radius);
        ring.fillStyle(COLOR_THREIA_GOLD, ringObj.alpha * 0.2);
        ring.fillCircle(this.player.x, this.player.y, ringObj.radius);
      },
      onComplete: () => {
        ring.destroy();
      },
    });

    // Particle burst
    try {
      const burst = this.scene.add.particles(this.player.x, this.player.y, TEX_GLOW, {
        lifespan: { min: 400, max: 800 },
        speed: { min: 20, max: 60 },
        scale: { start: 0.35, end: 0 },
        alpha: { start: 0.9, end: 0 },
        frequency: -1,
        quantity: 16,
      });
      burst.setDepth(this.player.depth + 3);
      burst.explode(16);

      this.scene.time.delayedCall(900, () => {
        if (burst) burst.destroy();
      });
    } catch (_) { /* fallback */ }

    // Remove tint after a moment
    this.scene.time.delayedCall(1000, () => {
      if (this.player && this.player.active) {
        this.player.clearTint();
        this.player.setAlpha(1);
      }
    });
  }

  /**
   * Show floating blessing text above the player.
   * @private
   * @param {string} message
   */
  _showBlessingText(message) {
    if (!this.scene || !this.player) return;

    const text = this.scene.add.text(this.player.x, this.player.y - 25, message, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '11px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3,
      align: 'center',
    })
      .setOrigin(0.5)
      .setDepth(510)
      .setScale(0.5)
      .setAlpha(0);

    // Pop in
    this.scene.tweens.add({
      targets: text,
      scale: 1.2,
      alpha: 1,
      duration: 400,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!text.scene) return;
        // Settle
        text.scene.tweens.add({
          targets: text,
          scale: 1,
          duration: 200,
          ease: 'Sine.easeOut',
        });

        // Float up and fade
        text.scene.tweens.add({
          targets: text,
          y: text.y - 40,
          alpha: 0,
          duration: 1200,
          delay: THREIA_BLESSING_TEXT_DURATION - 1200,
          ease: 'Quad.easeIn',
          onComplete: () => {
            text.destroy();
          },
        });
      },
    });
  }

  /**
   * Play a subtle hum/chime sound for each H press.
   * @private
   */
  _playHumSound() {
    if (!this.scene) return;
    try {
      const vol = getSfxVol(0.15 + (this._glowPhase / THREIA_PRAYER_GLOW_PHASES) * 0.25);
      this.scene.sound.play(AUDIO_THREIA_PRAYER_HUM, { volume: vol });
    } catch (_) { /* fallback */ }
  }

  /**
   * Clean up all visual effects.
   * @private
   */
  _cleanupEffects() {
    if (this._glowPulseTween) {
      this._glowPulseTween.stop();
      this._glowPulseTween = null;
    }
    if (this._glowGraphics) {
      this._glowGraphics.destroy();
      this._glowGraphics = null;
    }
    if (this._prayerEmitter) {
      const emitter = this._prayerEmitter;
      this._prayerEmitter = null;          // Null ref immediately to prevent orphaning
      emitter.stop();
      if (this.scene) {
        this.scene.time.delayedCall(700, () => {
          if (emitter && emitter.active) {
            emitter.destroy();
          }
        });
      } else {
        emitter.destroy();
      }
    }
    this._currentGlowAlpha = 0;
  }
}
